// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Conduit — the menu, the panels and the results table.
//
// THE DISMISSAL CONTRACT is implemented once, here, and every overlay in the game goes through it:
// a >=44px close control, a tap anywhere outside the panel, Escape, and a backdrop that is genuinely
// reachable. An overlay with one way out is a game you cannot play, and on a phone the button that
// opened it is underneath the thing covering it.

import { MODES, MODE_IDS, type ModeId } from './modes';
import { VENTS } from './plate';
import { gaugeName } from './pieces';
import type { Summary } from './session';

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

export const FOOTER_HTML = `<footer class="site-footer">
  Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
  · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>
</footer>`;

export const HOW_HTML = `
  <h2>How to play</h2>
  <p>Four fittings a round — the same four for everybody. Draw each one anywhere it fits.</p>
  <ul class="how">
    <li><b>Sides must meet like for like.</b> A culvert end can only meet a culvert end, a filament
      end only a filament end, and a solid side only another solid side.</li>
    <li><b>Everything splices.</b> Each fitting must join something already drawn, or run out through
      an edge vent of its own gauge. Your first fittings can only start at a vent.</li>
    <li><b>Seal vents.</b> A vent pays when its network reaches <em>another</em> vent. Bigger networks
      pay far more per vent, so joining beats scattering.</li>
    <li><b>Loose ends cost.</b> Any live end still facing an empty square at the finish is a penalty.</li>
    <li>Only the <b>coupler</b> joins the two gauges. There are about three in a run — spend them well.</li>
  </ul>
  <p class="fine">Tap a fitting, then tap or drag on the plate to aim it. Rotate, then Set.
    On a keyboard: 1–4 pick, arrows aim, R rotates, Enter sets, Z undoes.</p>`;

export const aboutHtml = (): string => `
  <h2>About Conduit</h2>
  <p>A plate, twelve edge vents and the same four fittings for everyone. What you do with them is the
    only thing that differs.</p>
  <p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a>.
    No accounts, no cookies, no trackers — just anonymous, cookie-less page counts.</p>
  <p class="fine">Playing with friends connects your browsers <b>directly to each other</b> over
    WebRTC. A public relay is used only to introduce them; no game state ever touches a server, and
    there is no server to touch. Connecting does reveal your IP address to the people in your room,
    which is how peer-to-peer works everywhere.</p>
  <p class="fine">The word list of fittings, the vent layout and every deal come from one shared seed,
    so every player gets an identical run and nothing about the game needs to cross the network.</p>`;

export function menuHtml(mode: ModeId, muted: boolean, best: number, streak: number): string {
  return `<div class="main-content menu">
    <h1 class="wordmark">Conduit</h1>
    <p class="tagline">Everyone gets the same four fittings. Only the plate is yours.</p>
    <div class="modepick" role="group" aria-label="Mode">
      ${MODE_IDS.map(
        (id) =>
          `<button type="button" class="mode${id === mode ? ' on' : ''}" data-mode="${id}">
             <b>${MODES[id].name}</b><span>${esc(MODES[id].blurb)}</span></button>`,
      ).join('')}
    </div>
    <div class="menubtns">
      <button id="playSolo" class="primary" type="button">Play</button>
      <button id="playFriends" class="secondary" type="button">Play with friends</button>
    </div>
    <p class="bestline">${best > 0 ? `Your best in ${MODES[mode].name}: <b>${best}</b>` : 'No run yet in this mode.'}${
      streak > 1 ? ` · ${streak}-day streak` : ''
    }</p>
    <div class="menufoot">
      <button id="howBtn" class="link" type="button">How to play</button>
      <button id="aboutBtn" class="link" type="button">About</button>
      <button id="muteBtn" class="link" type="button" aria-pressed="${muted}">${muted ? 'Sound off' : 'Sound on'}</button>
    </div>
  </div>${FOOTER_HTML}`;
}

/**
 * Open a panel that can always be closed.
 *
 * Four exits, every time. The backdrop listener is armed on a delay because a panel opened from a
 * pointerup handler is otherwise closed instantly by that same tap's trailing click.
 */
export function openPanel(host: HTMLElement, html: string): void {
  const back = document.createElement('div');
  back.className = 'backdrop';
  back.innerHTML = `<div class="sheet" role="dialog" aria-modal="true">
    <button class="sheetx" type="button" aria-label="Close">✕</button>${html}</div>`;
  host.append(back);
  const panel = back.querySelector('.sheet')!;
  const close = (): void => {
    document.removeEventListener('keydown', onKey);
    back.remove();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  back.querySelector('.sheetx')!.addEventListener('click', close);
  document.addEventListener('keydown', onKey);
  setTimeout(() => {
    back.addEventListener('pointerdown', (e) => {
      if (!panel.contains(e.target as Node)) close();
    });
  }, 350);
}

/**
 * Name a vent so a player can actually find it on the plate.
 *
 * Side plus gauge is NOT unique — the north and south edges each carry two culverts — so the first
 * version of this listed "south culvert, south culvert" among the vents nobody reached, which reads
 * as a rendering bug rather than as two different vents. The along-edge position disambiguates all
 * twelve.
 */
export const ventLabel = (v: number): string => {
  const vent = VENTS[v];
  const side = ['north', 'east', 'south', 'west'][vent.dir];
  const along = vent.dir === 0 || vent.dir === 2 ? vent.x : vent.y;
  const where = vent.dir === 0 || vent.dir === 2
    ? { 1: 'left', 3: 'middle', 5: 'right' }[along]
    : { 1: 'top', 3: 'middle', 5: 'bottom' }[along];
  return `${side} ${where} ${gaugeName(vent.gauge)}`;
};

/**
 * The results table.
 *
 * EVERY seat gets a row with a term-by-term breakdown — not a name and a number — because this is
 * the one moment players compare themselves, and "you scored 34" tells you nothing about what the
 * person next to you did differently. The vents NOBODY reached are listed too: that is the
 * "what everyone missed" line, and on a plate with a knowable best it is the most interesting row.
 */
export function summaryHtml(s: Summary): string {
  const rows = s.lines
    .map((l, i) => {
      const sc = l.score;
      const terms = sc
        ? [
            s.mode.ventsScore ? `<span>claim <b>${sc.claim}</b></span>` : '',
            s.mode.ventsScore ? `<span>networks <b>${sc.net}</b></span>` : '',
            `<span>run <b>${sc.run}</b></span>`,
            sc.offPath ? `<span>stray <b>${sc.offPath}</b></span>` : '',
            `<span>ends <b>${sc.ends}</b></span>`,
            sc.weep ? `<span>weeps <b>${sc.weep}</b></span>` : '',
            sc.scrap ? `<span>scrapped <b>${sc.scrap}</b></span>` : '',
          ]
            .filter(Boolean)
            .join('')
        : `<span class="fine">plate not received${l.away ? ' — left mid-run' : ''}</span>`;
      return `<li class="res${l.you ? ' you' : ''}">
        <div class="res-h"><span class="rank">${i + 1}</span>
          <i class="seatg" style="color:${l.ink}">${l.glyph}</i>
          <b>${esc(l.name)}${l.bot ? ' <em>bot</em>' : ''}${l.you ? ' <em>you</em>' : ''}</b>
          <u>${l.total}</u></div>
        <div class="res-t">${terms}</div>
        ${l.firsts > 0 ? `<div class="res-f">first to ${l.firsts} vent${l.firsts === 1 ? '' : 's'}</div>` : ''}
        ${l.mismatch ? `<div class="res-warn">build mismatch — one of you is on an older version</div>` : ''}
      </li>`;
    })
    .join('');

  const missed =
    s.missed.length > 0
      ? `<p class="missed"><b>Nobody reached:</b> ${s.missed.map(ventLabel).join(', ')}.</p>`
      : `<p class="missed">Between you, every vent on the plate was sealed.</p>`;

  return `<div class="main-content results">
    <h2>Run complete</h2>
    ${s.waiting ? `<p class="fine" id="resWaitPlates">Still hearing from the others…</p>` : ''}
    <ol class="reslist">${rows}</ol>
    ${missed}
    <div class="menubtns">
      <button id="againBtn" class="primary" type="button">Play again</button>
      <button id="lobbyBtn" class="secondary" type="button" hidden>Back to lobby</button>
      <button id="menuBtn" class="secondary" type="button">Menu</button>
    </div>
    <p class="fine" id="resWait" hidden></p>
    <p class="fine"><button class="link results-feedback" type="button">Something feel wrong? Tell me</button></p>
  </div>${FOOTER_HTML}`;
}
